/**
 * 预算文案 —— **G 域端侧的「中文名唯一源」**
 *
 * 依据：G 域 PRD **§4.5**（派生指标与呈现）· **§4.7**（三档建议）· `DR-011` §5（软提示文案）·
 * 铁律 8（色彩语义：**琥珀 = 接近 · 朱红 = 已超**，红色不表普通提示）。
 *
 * ## 🔴 本文件的边界：只做「数值 → 文本」
 *
 * | 做 | 不做 |
 * |---|---|
 * | 把服务端下发的 `usedE4` / `deviationE4` 变成 `71.5%` / `进度偏快 8.5%` | ❌ 不重算任何比例（`usedE4` 就是服务端算好的，端侧只是**排版**） |
 * | 档位 → 标签 / 配色槽 | ❌ 不判档（档位 `level` 由服务端给） |
 * | 建议档 → 标题 | ❌ 不挑默认档（`recommended` 由服务端给） |
 *
 * ⚠️ **百分比必须走整数运算**：本项目 eslint 把 `/` 与 `Math.trunc` 全项目封死
 * ⇒ 用共享层白名单的 `divideExact()` 做"留一位小数"的格式化（`7150` → `71.5%`）。
 */

import {
  BudgetLevel,
  BudgetPeriod,
  divideExact,
  type BudgetEventState,
  type BudgetProgressView,
  type BudgetSuggestionTier,
} from '@jiaying/shared';

// ─────────────────────────── 枚举 → 中文 ───────────────────────────

/** 周期（`AC-G1.1` 的四种） */
export const PERIOD_LABELS: Readonly<Record<BudgetPeriod, string>> = {
  [BudgetPeriod.Monthly]: '月度',
  [BudgetPeriod.Quarterly]: '季度',
  [BudgetPeriod.Yearly]: '年度',
  [BudgetPeriod.Event]: '事件',
};

/** 档位标签 —— 🔴 `safe` **没有标签**（安全态不该有个"正常"的徽标占位置） */
export const LEVEL_LABELS: Readonly<Record<BudgetLevel, string | null>> = {
  [BudgetLevel.Safe]: null,
  [BudgetLevel.Warn]: '接近',
  [BudgetLevel.Over]: '已超',
};

/**
 * 档位 → 配色槽（铁律 8）。
 *
 * | 档 | 槽 | 令牌 |
 * |---|---|---|
 * | `safe` | 品牌色 | `--c-brand-fill` |
 * | `warn` | **琥珀** | `--c-notice` |
 * | `over` | **朱红** | `--c-alert` |
 */
export const LEVEL_COLORS: Readonly<Record<BudgetLevel, 'primary' | 'warning' | 'error'>> = {
  [BudgetLevel.Safe]: 'primary',
  [BudgetLevel.Warn]: 'warning',
  [BudgetLevel.Over]: 'error',
};

/** `event` 窗口状态（§4.2 / 原型「未开始」态） */
export const EVENT_STATE_LABELS: Readonly<Record<BudgetEventState, string>> = {
  notStarted: '未开始',
  ongoing: '进行中',
  ended: '已结束',
};

/** 三档建议的标题（`AC-G1.16`） */
export const SUGGESTION_LABELS: Readonly<Record<BudgetSuggestionTier, string>> = {
  lastWindow: '上一个同周期',
  recentAverage: '近 3 期均值',
  incomeShare: '收入 30%',
};

// ─────────────────────────── 数值 → 文本 ───────────────────────────

/**
 * E4 比率 → 百分比文本（**保留一位小数，整数为整时不带 `.0`**）。
 *
 * `7150` → `71.5%` · `10000` → `100%` · `0` → `0%`
 */
export function percentText(e4: number): string {
  // 🔴 必须**先取绝对值再拆十位**：`divideExact` 是**四舍五入**的，
  //    若直接 `divideExact(tenths, 10)` 拿整数位，`1235` 会被舍成 `124`、余数变 `-5`
  //    （实测得到 `124.-5%`）。⇒ 余数用 `%` 分离、整数位用**整除**（`tenths - frac` 必被 10 整除）。
  const negative = e4 < 0;
  const abs = negative ? -e4 : e4;

  const tenths = divideExact(abs * 10, 100); // 例：7150 → 715（= 71.5% 的"十分位整数"）
  const frac = tenths % 10;
  const whole = divideExact(tenths - frac, 10);

  const text = frac === 0 ? `${whole}%` : `${whole}.${frac}%`;
  return negative ? `-${text}` : text;
}

/**
 * 偏离文本（`AC-G1.11`：`已用% − 时间进度%`）。
 *
 * 🔴 **只陈述快慢，不做评价**（"进度偏快 8.5%" 而不是"花太多了"）——
 * 与成员维度同一纪律（`DR-011` §7：文案禁判断句）。
 */
export function deviationText(deviationE4: number): string {
  // 0.1 个百分点（= 10 E4）以内的偏离算"正常"，避免 0.03% 也报一个方向
  if (deviationE4 > -10 && deviationE4 < 10) return '进度正常';
  const abs = deviationE4 < 0 ? -deviationE4 : deviationE4;
  return deviationE4 > 0 ? `进度偏快 ${percentText(abs)}` : `进度偏慢 ${percentText(abs)}`;
}

/**
 * 超支金额（`AC-G1.8`：卡面显示"超出 ¥690"）。
 *
 * 🔴 由**服务端算好的** `remaining` 取绝对值 ⇒ 返回**正数分**（`null` = 没超）。
 * ⚠️ 只给数字，**不拼币种符** —— 渲染必须走 `Amount`（`DR-036`：金额必带币种，
 * 手写符号表就是第二份实现，lint 会拦）。
 */
export function overspentAmount(progress: BudgetProgressView): number | null {
  const remaining = progress.remaining as number;
  return remaining < 0 ? -remaining : null;
}

/**
 * 「含待缴 ¥X 未付」标注（`AC-G1.13`）。
 *
 * 🔴 **仅当 `X > 0` 时返回文本**，否则 `null`（不显示）——「¥0 未付」是纯噪音。
 * 🔴 金额文本由 `Amount` 组件渲染（**带币种**，`DR-036`）⇒ 本函数只给**数字**。
 */
export function unpaidAmount(progress: BudgetProgressView): number | null {
  const v = progress.unpaidPending as number;
  return v > 0 ? v : null;
}

// ─────────────────────────── 固定文案 ───────────────────────────

/** 缺汇率的合计不完整提示（架构 §5.4 铁律 5：缺汇率**不静默、不按 1:1 冒充**） */
export const FX_INCOMPLETE_BUDGET_NOTE =
  '部分外币支出缺少当日汇率，未计入本币合计 —— 合计可能偏小。可在「设置 · 汇率」补录。';

/** 软提示前缀（`AC-G1.28`：**软提示、不拦截**，且必须给出两个具体数字） */
export const SOFT_HINT_LEAD = '分类预算合计';

/**
 * 🆕 建议区标题（批 `109`）—— 总预算与分类预算的**参照对象不同**，标题必须跟着变。
 *
 * 🔴 总预算的建议来自「全部分类的支出合计」（服务端 `budgetSpentFrom(…, BudgetTotalKey)`），
 * 分类预算的建议来自「该分类的支出」。标题写错会让人以为总预算建议的是某个分类的数。
 */
export const SUGGESTION_TITLE_CATEGORY = '参考：这个分类过去花了多少';
export const SUGGESTION_TITLE_TOTAL = '参考：全部支出过去花了多少';

/**
 * 🆕 「总预算低于分类合计」的提示主体（批 `109` · `AC-G1.28` 的同一条纪律：**只提示不拦截**）。
 *
 * 场景：编辑总预算时把额度调到低于已建分类预算之和 —— 原来的软提示只覆盖"新增分类预算导致合计超出"，
 * 这个方向没有提示，用户会以为"分类之和仍受总预算约束"。两个数字照旧都给。
 */
export const TOTAL_BELOW_SUM_HINT =
  '已经超过你要设的总预算 —— 分类预算之和大于总预算时，总预算这条线就失去意义了。';

/** 无预算时的引导（**不显示数字**，只给下一步动作 —— `AC-G1.23` 的同一条纪律：不画空卡片） */
export const NO_BUDGET_TITLE = '还没有设预算';
export const NO_BUDGET_HINT = '设一个总预算，就知道这个月"还能花多少"；也可以只给某几个分类定额度。';

/** 成员维度标题（`AC-G1.21`） */
export const MEMBER_SECTION_TITLE = '成员维度';
export const MEMBER_SECTION_SUB = '本月窗口内支出';
/** 🔴 「共同（未归因）」行 —— 当前模型下恒为 0，但**保留这一行**（`AC-G1.21` 明文要求） */
export const MEMBER_UNATTRIBUTED_LABEL = '共同（未归因）';

/** 列表空态 */
export const EMPTY_BUDGET_TITLE = '还没有任何预算';
export const EMPTY_BUDGET_HINT = '预算不是限制，是"心里有数"—— 先给最常花的那个分类定一个额度。';
